import React, { useState, useMemo } from "react";
import useCustomFields from "../hooks/useCustomFields";
import {
  STANDARD_COLUMN_LABELS,
  DEFAULT_REORDERABLE_ORDER,
  FIELD_TYPES,
} from "../hooks/useCustomFields";
import "./CustomFieldsSettings.css";

function getEffectiveOrder(savedOrder, customFieldKeys) {
  const standardKeys = Object.keys(STANDARD_COLUMN_LABELS);
  const allKeys = new Set([...standardKeys, ...customFieldKeys]);

  if (!savedOrder || savedOrder.length === 0) {
    const order = [...DEFAULT_REORDERABLE_ORDER];
    const templateIdx = order.indexOf("lead_template");
    if (templateIdx >= 0) {
      order.splice(templateIdx, 0, ...customFieldKeys);
    } else {
      order.push(...customFieldKeys);
    }
    return order;
  }

  const filtered = savedOrder.filter((key) => allKeys.has(key));
  const existing = new Set(filtered);
  const missing = [...allKeys].filter((key) => !existing.has(key));
  return [...filtered, ...missing];
}

const CustomFieldsSettings = ({ userId }) => {
  const {
    customFields,
    hiddenFields,
    loading,
    addField,
    updateField,
    hideField,
    unhideField,
    deleteFieldPermanently,
    saveColumnOrder,
    savedColumnOrder,
    MAX_CONTEXT_FIELDS,
    MAX_TOTAL_FIELDS,
  } = useCustomFields(userId);

  const [newLabel, setNewLabel] = useState("");
  const [newFieldType, setNewFieldType] = useState("text");
  const [newUseContext, setNewUseContext] = useState(false);
  const [addError, setAddError] = useState("");
  const [isAdding, setIsAdding] = useState(false);
  const [editingFieldId, setEditingFieldId] = useState(null);
  const [editingLabel, setEditingLabel] = useState("");
  const [deleteConfirm, setDeleteConfirm] = useState(null);
  const [isDeleting, setIsDeleting] = useState(false);
  const [deleteAcknowledged, setDeleteAcknowledged] = useState(false);

  const contextCount = customFields.filter((f) => f.useAsEmailContext).length;
  const totalFieldCount = customFields.length + hiddenFields.length;
  const totalLimitReached = totalFieldCount >= MAX_TOTAL_FIELDS;
  const customFieldKeys = useMemo(() => customFields.map((cf) => cf.key), [customFields]);

  const effectiveOrder = useMemo(
    () => getEffectiveOrder(savedColumnOrder, customFieldKeys),
    [savedColumnOrder, customFieldKeys]
  );

  const customFieldMap = useMemo(() => {
    const map = {};
    customFields.forEach((cf) => { map[cf.key] = cf; });
    return map;
  }, [customFields]);

  const handleAdd = async () => {
    if (!newLabel.trim()) return;
    setAddError("");
    setIsAdding(true);

    const result = await addField(newLabel, newUseContext, newFieldType);
    if (!result.success) {
      setAddError(result.error);
    } else {
      setNewLabel("");
      setNewFieldType("text");
      setNewUseContext(false);
    }
    setIsAdding(false);
  };

  const handleToggleContext = async (field) => {
    const result = await updateField(field.id, {
      useAsEmailContext: !field.useAsEmailContext,
    });
    if (result && !result.success) {
      alert(result.error);
    }
  };

  const handleStartRename = (field) => {
    setEditingFieldId(field.id);
    setEditingLabel(field.label);
  };

  const handleCancelRename = () => {
    setEditingFieldId(null);
    setEditingLabel("");
  };

  const handleSaveRename = async (field) => {
    const trimmed = editingLabel.trim();
    if (!trimmed || trimmed === field.label) {
      handleCancelRename();
      return;
    }
    const result = await updateField(field.id, { label: trimmed });
    if (result && !result.success) {
      alert(result.error);
    } else {
      handleCancelRename();
    }
  };

  const handleHide = async (field) => {
    const result = await hideField(field.id);
    if (result && !result.success) {
      alert(result.error);
    }
  };

  const handleUnhide = async (field) => {
    const result = await unhideField(field.id);
    if (result && !result.success) {
      alert(result.error);
    }
  };

  const handlePermanentDelete = async (field) => {
    setIsDeleting(true);
    try {
      const result = await deleteFieldPermanently(field.id);
      if (result && !result.success) {
        alert(result.error);
      }
    } catch (err) {
      console.error("Error deleting field:", err);
      alert("Failed to delete field. Please try again.");
    } finally {
      setDeleteConfirm(null);
      setIsDeleting(false);
      setDeleteAcknowledged(false);
    }
  };

  const handleMoveColumn = (index, direction) => {
    const targetIndex = direction === "up" ? index - 1 : index + 1;
    if (targetIndex < 0 || targetIndex >= effectiveOrder.length) return;
    const newOrder = [...effectiveOrder];
    [newOrder[index], newOrder[targetIndex]] = [newOrder[targetIndex], newOrder[index]];
    saveColumnOrder(newOrder);
  };

  if (loading) {
    return (
      <div className="cf-settings">
        <div className="cf-settings-loading">Loading custom fields...</div>
      </div>
    );
  }

  return (
    <div className="cf-settings">
      <div className="cf-hero">
        <div className="cf-hero-icon">
          <svg width="26" height="26" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <rect x="3" y="3" width="18" height="18" rx="2" />
            <line x1="3" y1="9" x2="21" y2="9" />
            <line x1="9" y1="3" x2="9" y2="21" />
          </svg>
        </div>
        <div className="cf-hero-text">
          <h3>Custom Fields & Column Order</h3>
          <p>
            Add custom columns and arrange all table columns in the order you
            prefer. Fields marked as AI context are included in email generation.
          </p>
        </div>
        <div className="cf-hero-stats">
          <div className="cf-hero-stat">
            <span className="cf-hero-stat-number">{totalFieldCount}/{MAX_TOTAL_FIELDS}</span>
            <span className="cf-hero-stat-label">Custom</span>
          </div>
          <div className="cf-hero-stat">
            <span className="cf-hero-stat-number">{contextCount}/{MAX_CONTEXT_FIELDS}</span>
            <span className="cf-hero-stat-label">AI Context</span>
          </div>
        </div>
      </div>

      {/* Add Custom Field */}
      <div className="cf-add-section">
        <div className="cf-add-row">
          <input
            type="text"
            className="cf-add-input"
            placeholder={totalLimitReached ? `Maximum ${MAX_TOTAL_FIELDS} fields reached` : "Field name (e.g. Industry, Revenue...)"}
            value={newLabel}
            onChange={(e) => {
              setNewLabel(e.target.value);
              setAddError("");
            }}
            onKeyDown={(e) => e.key === "Enter" && !totalLimitReached && handleAdd()}
            maxLength={40}
            disabled={totalLimitReached}
          />
          <div className="cf-add-type-select">
            {Object.entries(FIELD_TYPES).map(([typeKey, typeInfo]) => (
              <button
                key={typeKey}
                type="button"
                className={`cf-type-option ${newFieldType === typeKey ? "cf-type-option-active" : ""}`}
                onClick={() => setNewFieldType(typeKey)}
              >
                <span className="cf-type-option-icon">{typeInfo.icon}</span>
                <span>{typeInfo.label}</span>
              </button>
            ))}
          </div>
          <label className="cf-add-context-toggle">
            <input
              type="checkbox"
              checked={newUseContext}
              onChange={(e) => setNewUseContext(e.target.checked)}
              disabled={contextCount >= MAX_CONTEXT_FIELDS}
            />
            <span className="cf-add-context-label">Use as AI Context</span>
          </label>
          <button
            className="cf-add-btn"
            onClick={handleAdd}
            disabled={isAdding || !newLabel.trim() || totalLimitReached}
          >
            {isAdding ? "Adding..." : "Add Field"}
          </button>
        </div>
        {addError && <div className="cf-add-error">{addError}</div>}
      </div>

      {/* Column Order */}
      <div className="cf-order-section">
        <div className="cf-order-header">
          <h4>Column Order</h4>
          <p>Move columns up or down to change their order in the table.</p>
        </div>
        <div className="cf-order-list">
          {effectiveOrder.map((key, index) => {
            const isCustom = key.startsWith("cf_");
            const cfData = isCustom ? customFieldMap[key] : null;
            const label = isCustom ? (cfData?.label || key) : (STANDARD_COLUMN_LABELS[key] || key);

            return (
              <div key={key} className={`cf-order-item ${isCustom ? "cf-order-item-custom" : ""}`}>
                <div className="cf-order-item-arrows">
                  <button
                    className="cf-order-arrow-btn"
                    onClick={() => handleMoveColumn(index, "up")}
                    disabled={index === 0}
                    title="Move up"
                  >
                    <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                      <polyline points="18 15 12 9 6 15" />
                    </svg>
                  </button>
                  <button
                    className="cf-order-arrow-btn"
                    onClick={() => handleMoveColumn(index, "down")}
                    disabled={index === effectiveOrder.length - 1}
                    title="Move down"
                  >
                    <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                      <polyline points="6 9 12 15 18 9" />
                    </svg>
                  </button>
                </div>

                <span className="cf-order-item-pos">{index + 1}</span>

                <div className="cf-order-item-info">
                  {isCustom && cfData && editingFieldId === cfData.id ? (
                    <div className="cf-rename-inline">
                      <input
                        className="cf-rename-input"
                        value={editingLabel}
                        onChange={(e) => setEditingLabel(e.target.value)}
                        onKeyDown={(e) => {
                          if (e.key === "Enter") handleSaveRename(cfData);
                          if (e.key === "Escape") handleCancelRename();
                        }}
                        onBlur={() => handleSaveRename(cfData)}
                        maxLength={40}
                        autoFocus
                      />
                      <button
                        className="cf-rename-save-btn"
                        onMouseDown={(e) => { e.preventDefault(); handleSaveRename(cfData); }}
                        title="Save"
                      >
                        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                          <polyline points="20 6 9 17 4 12" />
                        </svg>
                      </button>
                      <button
                        className="cf-rename-cancel-btn"
                        onMouseDown={(e) => { e.preventDefault(); handleCancelRename(); }}
                        title="Cancel"
                      >
                        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                          <line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" />
                        </svg>
                      </button>
                    </div>
                  ) : (
                    <>
                      <span className="cf-order-item-label">{label}</span>
                      {isCustom && cfData && (
                        <button className="cf-rename-btn" onClick={() => handleStartRename(cfData)} title="Rename field">
                          <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                            <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" />
                            <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z" />
                          </svg>
                        </button>
                      )}
                      <span className={`cf-order-item-badge ${isCustom ? "cf-order-item-badge-custom" : "cf-order-item-badge-standard"}`}>
                        {isCustom ? "Custom" : "Standard"}
                      </span>
                      {isCustom && cfData && (
                        <span className={`cf-order-item-type cf-order-item-type-${cfData.fieldType || "text"}`}>
                          {FIELD_TYPES[cfData.fieldType || "text"]?.icon} {FIELD_TYPES[cfData.fieldType || "text"]?.label}
                        </span>
                      )}
                    </>
                  )}
                </div>

                <div className="cf-order-item-actions">
                  {isCustom && cfData && (
                    <>
                      <label
                        className={`cf-context-badge ${cfData.useAsEmailContext ? "active" : ""}`}
                        title="Include in AI email generation context"
                      >
                        <input
                          type="checkbox"
                          checked={cfData.useAsEmailContext || false}
                          onChange={() => handleToggleContext(cfData)}
                        />
                        <span>AI Context</span>
                      </label>
                      <button
                        className="cf-hide-btn"
                        onClick={() => handleHide(cfData)}
                        title="Hide field"
                      >
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                          <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94" />
                          <path d="M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19" />
                          <line x1="1" y1="1" x2="23" y2="23" />
                        </svg>
                      </button>
                    </>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Hidden Fields */}
      {hiddenFields.length > 0 && (
        <div className="cf-hidden-section">
          <div className="cf-hidden-header">
            <div className="cf-hidden-header-icon">
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94" />
                <path d="M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19" />
                <line x1="1" y1="1" x2="23" y2="23" />
              </svg>
            </div>
            <div>
              <h4>Hidden Fields ({hiddenFields.length})</h4>
              <p>These fields are not visible in the table. Data is preserved until permanently deleted.</p>
            </div>
          </div>
          <div className="cf-hidden-list">
            {hiddenFields.map((field) => (
              <div key={field.id} className="cf-hidden-item">
                <div className="cf-hidden-item-info">
                  <span className="cf-hidden-item-label">{field.label}</span>
                  <div className="cf-hidden-item-meta">
                    <span className="cf-hidden-item-key">{field.key}</span>
                    <span className={`cf-order-item-type cf-order-item-type-${field.fieldType || "text"}`}>
                      {FIELD_TYPES[field.fieldType || "text"]?.icon} {FIELD_TYPES[field.fieldType || "text"]?.label}
                    </span>
                  </div>
                </div>
                <div className="cf-hidden-item-actions">
                  <button
                    className="cf-restore-btn"
                    onClick={() => handleUnhide(field)}
                    title="Restore field"
                  >
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" />
                      <circle cx="12" cy="12" r="3" />
                    </svg>
                    <span>Restore</span>
                  </button>
                  <button
                    className="cf-permanent-delete-btn"
                    onClick={() => { setDeleteConfirm(field); setDeleteAcknowledged(false); }}
                    title="Permanently delete field and all data"
                  >
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <polyline points="3 6 5 6 21 6" />
                      <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" />
                    </svg>
                    <span>Delete Permanently</span>
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Permanent Delete Confirmation Modal */}
      {deleteConfirm && (
        <div className="cf-delete-overlay" onClick={() => !isDeleting && setDeleteConfirm(null)}>
          <div className="cf-delete-modal" onClick={(e) => e.stopPropagation()}>
            <div className="cf-delete-modal-icon">
              <svg width="42" height="42" viewBox="0 0 24 24" fill="none">
                <circle cx="12" cy="12" r="11" stroke="#ef4444" strokeWidth="1.5" fill="#fef2f2" />
                <path d="M12 8v4" stroke="#dc2626" strokeWidth="2" strokeLinecap="round" />
                <circle cx="12" cy="15.5" r="1" fill="#dc2626" />
              </svg>
            </div>
            <h3 className="cf-delete-modal-title">Permanently Delete Field</h3>
            <p className="cf-delete-modal-text">
              You are about to permanently delete <strong>"{deleteConfirm.label}"</strong>.
            </p>
            <div className="cf-delete-modal-consequences">
              <div className="cf-delete-modal-consequence">
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#dc2626" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" /></svg>
                <span>The field definition will be removed</span>
              </div>
              <div className="cf-delete-modal-consequence">
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#dc2626" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" /></svg>
                <span>All data in this column across <strong>all contacts</strong> will be erased</span>
              </div>
              <div className="cf-delete-modal-consequence">
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#dc2626" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" /></svg>
                <span>No other contact data will be affected</span>
              </div>
            </div>
            <label className="cf-delete-modal-acknowledge">
              <input
                type="checkbox"
                checked={deleteAcknowledged}
                onChange={(e) => setDeleteAcknowledged(e.target.checked)}
                disabled={isDeleting}
              />
              <span>I understand this action is irreversible</span>
            </label>
            <div className="cf-delete-modal-actions">
              <button
                className="cf-delete-modal-cancel"
                onClick={() => { setDeleteConfirm(null); setDeleteAcknowledged(false); }}
                disabled={isDeleting}
              >
                Cancel
              </button>
              <button
                className="cf-delete-modal-confirm"
                onClick={() => handlePermanentDelete(deleteConfirm)}
                disabled={isDeleting || !deleteAcknowledged}
              >
                {isDeleting ? "Deleting..." : "Delete Permanently"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default CustomFieldsSettings;
